import { useRef } from 'react';
import { useFormik } from 'formik';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { Box } from '@/components/Box/Box';
import { Button } from '@/components/Button/Button';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { Typography } from '@/components/Typography/Typography';
import { COLORS } from '@/styles/colors';
import { FormikCheckbox } from '@/components/form/fields/FormikCheckbox/FormikCheckbox';
import { MASTER_SERVICE_AGREEMENT_VALIDATION_SCHEMA } from '../const';
import { isActiveSnackbar, showSnackbar } from '@/components/Snackbar/Snackbar';

export type AcceptMasterServiceAgreementFormValuesType = Omit<FormType, 'confirmed_agreement'>;

type AcceptMasterServiceAgreementFormType = {
  isDisabled: boolean;
  isLoading: boolean;
  onConfirm: (payload: AcceptMasterServiceAgreementFormValuesType) => void;
};

type FormType = {
  first_name: string;
  middle_name: string;
  last_name: string;
  confirmed_agreement: boolean;
};

const CONFIRMED_AGREEMENT_CHECKBOX_LABEL =
  'I have read and agree to the Master Service Agreement. I understand that by submitting my name below, I am electronically signing this agreement and it is legally binding.';

export const AcceptMasterServiceAgreementForm = ({
  isDisabled,
  onConfirm,
  isLoading,
}: AcceptMasterServiceAgreementFormType) => {
  const snackbarIdRef = useRef(null);
  const formik = useFormik<FormType>({
    initialValues: {
      first_name: '',
      middle_name: '',
      last_name: '',
      confirmed_agreement: false,
    },
    validationSchema: MASTER_SERVICE_AGREEMENT_VALIDATION_SCHEMA,
    onSubmit: (values) => {
      const payload = {
        first_name: values.first_name,
        middle_name: values.middle_name,
        last_name: values.last_name,
      };

      return onConfirm(payload);
    },
  });

  const confirmAgreementPolicy = () => {
    const isSnackbarVisible = isActiveSnackbar(snackbarIdRef?.current);

    // Show snackbar error when user tries to accept it without reading it fully.
    // Note: show only one snackbar until previous one is disappeared
    if (isDisabled && !isSnackbarVisible) {
      const snackbarId = showSnackbar('You must read the full Master Service Agreement before you can accept it.', {
        variant: 'error',
      });

      snackbarIdRef.current = snackbarId;
    }
  };

  return (
    <FormikForm value={formik}>
      <Box width='100%' display='flex' flexDirection='column' rowGap='8px'>
        <Box display='flex' flexDirection='column' rowGap='10px'>
          <Box display='flex' columnGap='16px' alignItems='start' onClick={confirmAgreementPolicy}>
            <FormikCheckbox
              name='confirmed_agreement'
              checked={formik.values.confirmed_agreement}
              disabled={isDisabled}
              label={<Typography color={COLORS.grey[950]}>{CONFIRMED_AGREEMENT_CHECKBOX_LABEL}</Typography>}
            />
          </Box>
          <Typography variant='bodySmall' color={COLORS.grey[650]}>
            Enter your legal name to provide your electronic signature.
          </Typography>
        </Box>
        <Box display='flex' flexDirection='row' height='100%' columnGap='18px' mobile={{ flexDirection: 'column' }}>
          <Box display='flex' alignItems='center' columnGap='8px'>
            <FormikInput name='first_name' placeholder='First Name*' disabled={isDisabled} />
            <FormikInput name='middle_name' placeholder='Middle Name' disabled={isDisabled} />
            <FormikInput name='last_name' placeholder='Last Name*' disabled={isDisabled} />
          </Box>
          <Box display='flex' height='100%'>
            <Button width='240px' mobileStretch type='submit' disabled={isDisabled} isLoading={isLoading}>
              Accept Agreement
            </Button>
          </Box>
        </Box>
      </Box>
    </FormikForm>
  );
};
